import { CheckCircle2, Eye, FileText, Loader2, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState, type ChangeEvent } from "react";

import { axiosInstance } from "@/shared/lib/axiosInstance";
import { showToast } from "@/shared/lib/toast";
import { uploadOcrDocument } from "@/shared/services/ocr";
import { extractStructuredOcrData } from "@/shared/utils/documentOcr";
import { App_Url_New } from "../common/static";
import { BooleanRecord } from "../types";
import {
  DocumentUploadField,
  DocumentUploadGridProps,
  FieldKey,
} from "../types/documentuploadtype";
import { Checkbox } from "./Checkbox";
import { WrapperInput } from "./WrapperInput";

function appendFormFields(
  formData: FormData,
  fields?: Record<string, string | number | boolean | null | undefined>
) {
  Object.entries(fields || {}).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      formData.append(key, String(value));
    }
  });
}

function getDocumentLabel<TValues extends BooleanRecord>(field: DocumentUploadField<TValues>) {
  return field.label || field.title || "Document";
}

function getUploadSuccessMessage<TValues extends BooleanRecord>(
  field: DocumentUploadField<TValues>
) {
  return `${getDocumentLabel(field)} uploaded successfully.`;
}

function getUploadWithOcrWarningMessage<TValues extends BooleanRecord>(
  field: DocumentUploadField<TValues>
) {
  return `${getDocumentLabel(field)} uploaded successfully, but extraction failed.`;
}

function getUploadFailureMessage<TValues extends BooleanRecord>(
  field: DocumentUploadField<TValues>
) {
  return `${getDocumentLabel(field)} upload failed. Please try again.`;
}

function getDocumentUploadToast<TValues extends BooleanRecord>(
  field: DocumentUploadField<TValues>,
  ocrStatus: "completed" | "failed" | "skipped" | "not_requested"
) {
  if (ocrStatus === "failed") {
    return {
      message: getUploadWithOcrWarningMessage(field),
      type: "warning" as const,
    };
  }

  return {
    message: getUploadSuccessMessage(field),
    type: "success" as const,
  };
}

export function DocumentUploadGrid<TValues extends BooleanRecord>({
  fields,
  values,
  onChange,
  disabled = false,
  gridClassName,
  label,
  description,
  error,
  fieldErrors: externalFieldErrors,
  uploadEndpoint,
  uploadFileFieldName = "file",
  uploadFields,
  ocrEndpointPath,
  ocrFileFieldName = "files",
  ocrFields,
  accept = App_Url_New.defaultAccept,
  maxFileSizeMb = 5,
  initialDocuments,
  showPreviewButton = false,
  showDeleteButton = true,
  showDownloadButton = false,
  onPreviewClick,
  onDownloadClick,
  onUploadComplete,
  onRemoveDocument,
}: DocumentUploadGridProps<TValues> & {
  showDownloadButton?: boolean;
  onDownloadClick?: (params: any) => void;
}) {
  const [files, setFiles] = useState<Partial<Record<FieldKey<TValues>, File>>>({});
  const [previews, setPreviews] = useState<Partial<Record<FieldKey<TValues>, string>>>({});
  const [processing, setProcessing] = useState<Partial<Record<FieldKey<TValues>, boolean>>>({});
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldKey<TValues>, string>>>({});
  const [removedInitialDocs, setRemovedInitialDocs] = useState<Record<string, boolean>>({});
  const [combinedUploadGroups, setCombinedUploadGroups] = useState<Record<string, boolean>>({});
  const [touchedCombinedGroups, setTouchedCombinedGroups] = useState<Record<string, boolean>>({});

  const groupFieldsById = useMemo(() => {
    return fields.reduce<Record<string, Array<DocumentUploadField<TValues>>>>((groups, field) => {
      const groupId = field.combinedUploadGroup?.id;
      if (!groupId) {
        return groups;
      }

      groups[groupId] = [...(groups[groupId] || []), field];
      return groups;
    }, {});
  }, [fields]);

  useEffect(() => {
    setCombinedUploadGroups((current) => {
      const nextGroups = { ...current };

      Object.entries(groupFieldsById).forEach(([groupId, groupFields]) => {
        if (touchedCombinedGroups[groupId]) {
          return;
        }

        const primaryField =
          groupFields.find((groupField) => groupField.combinedUploadGroup?.isPrimary) ||
          groupFields[0];
        const allFieldsComplete = groupFields.every(
          (groupField) => values[groupField.key] === true
        );
        const hasSecondaryDocument = groupFields
          .filter((groupField) => groupField.key !== primaryField?.key)
          .some((groupField) => Boolean(initialDocuments?.[groupField.key]));

        const groupKey = primaryField?.combinedUploadGroup?.key;
        const isCombinedFromValues = groupKey ? values[groupKey] === true : false;

        nextGroups[groupId] = isCombinedFromValues || (allFieldsComplete && !hasSecondaryDocument);
      });

      return nextGroups;
    });
  }, [groupFieldsById, initialDocuments, touchedCombinedGroups, values]);

  useEffect(() => {
    return () => {
      Object.values(previews).forEach((preview) => {
        if (preview) {
          URL.revokeObjectURL(preview);
        }
      });
    };
  }, [previews]);

  const updateCombinedGroupValues = (
    field: DocumentUploadField<TValues>,
    nextValues: TValues,
    uploaded: boolean
  ) => {
    const groupId = field.combinedUploadGroup?.id;
    if (!groupId || combinedUploadGroups[groupId] !== true) {
      return nextValues;
    }

    const groupFields = groupFieldsById[groupId] || [];
    const updatedValues = { ...nextValues };
    groupFields.forEach((groupField) => {
      updatedValues[groupField.key] = uploaded as TValues[typeof groupField.key];
    });

    const groupKey = field.combinedUploadGroup?.key;
    if (groupKey) {
      updatedValues[groupKey] = uploaded as TValues[typeof groupKey];
    }

    return updatedValues;
  };

  const getAffectedFields = (field: DocumentUploadField<TValues>) => {
    const groupId = field.combinedUploadGroup?.id;
    if (!groupId || combinedUploadGroups[groupId] !== true) {
      return [field];
    }

    return groupFieldsById[groupId] || [field];
  };

  const handleFileChange =
    (field: DocumentUploadField<TValues>) => async (event: ChangeEvent<HTMLInputElement>) => {
      if (disabled) {
        event.preventDefault();
        event.stopPropagation();
        event.target.value = "";
        return;
      }

      const file = event.target.files?.[0];
      event.target.value = "";

      if (!file) {
        return;
      }

      if (file.size > maxFileSizeMb * 1024 * 1024) {
        showToast(`File size must be less than ${maxFileSizeMb} MB.`, "error");
        setFieldErrors((current) => ({
          ...current,
          [field.key]: `File size must be less than ${maxFileSizeMb} MB`,
        }));
        return;
      }

      setProcessing((current) => ({ ...current, [field.key]: true }));
      setFieldErrors((current) => ({ ...current, [field.key]: "" }));
      let toastPayload:
        | {
            message: string;
            type: "success" | "error" | "warning";
          }
        | undefined;

      try {
        let uploadResponse: unknown;
        let ocrResponse: unknown;
        let ocrStatus: "completed" | "failed" | "skipped" | "not_requested" = ocrEndpointPath
          ? "failed"
          : "not_requested";

        if (uploadEndpoint) {
          const formData = new FormData();
          formData.append(uploadFileFieldName, file);
          appendFormFields(formData, uploadFields);
          appendFormFields(formData, field.uploadFields);

          const response = await axiosInstance.post(uploadEndpoint, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
          uploadResponse = response.data;
        }

        if (ocrEndpointPath && field.enableOcr !== false) {
          try {
            ocrResponse = await uploadOcrDocument({
              endpointPath: ocrEndpointPath,
              fields: {
                ...ocrFields,
                ...field.ocrFields,
              },
              files: [{ name: ocrFileFieldName, file }],
            });
            ocrStatus = "completed";
          } catch (ocrError) {
            console.error("Document OCR failed:", ocrError);
            ocrStatus = "failed";
            setFieldErrors((current) => ({
              ...current,
              [field.key]: "Document uploaded, but analysis failed. You can continue or re-upload.",
            }));
          }
        } else if (field.enableOcr === false) {
          ocrStatus = "skipped";
        }

        toastPayload = getDocumentUploadToast(field, ocrStatus);

        setFiles((current) => ({ ...current, [field.key]: file }));
        setPreviews((current) => {
          const previousPreview = current[field.key];
          if (previousPreview) {
            URL.revokeObjectURL(previousPreview);
          }

          return {
            ...current,
            [field.key]: file.type.startsWith("image/") ? URL.createObjectURL(file) : "",
          };
        });

        onChange(updateCombinedGroupValues(field, { ...values, [field.key]: true }, true));

        try {
          onUploadComplete?.({
            field,
            affectedFields: getAffectedFields(field),
            file,
            uploadResponse,
            ocrResponse,
            persistedDocument: {
              fieldKey: field.key,
              filename: file.name,
              fileName: file.name,
              mimeType: file.type,
              size: file.size,
              uploadedAt: new Date().toISOString(),
              uploadResponse,
              ocrResponse,
              ocrEnabled: field.enableOcr !== false,
              ocrRequested: Boolean(ocrEndpointPath) && field.enableOcr !== false,
              ocrStatus,
              insuranceType:
                typeof field.ocrFields?.insurance_type === "string"
                  ? field.ocrFields.insurance_type
                  : undefined,
              structuredData: extractStructuredOcrData(ocrResponse),
              upload: {
                fileName: file.name,
                response: uploadResponse,
              },
              ocr: {
                response: ocrResponse,
                status: ocrStatus,
                insuranceType:
                  typeof field.ocrFields?.insurance_type === "string"
                    ? field.ocrFields.insurance_type
                    : undefined,
                structuredData: extractStructuredOcrData(ocrResponse),
              },
            },
          });
        } catch (uploadCompleteError) {
          console.error("Document upload completion handler failed:", uploadCompleteError);
        }
      } catch (uploadError) {
        console.error("Document upload failed:", uploadError);
        toastPayload = {
          message: getUploadFailureMessage(field),
          type: "error",
        };
        setFieldErrors((current) => ({
          ...current,
          [field.key]: "Failed to upload document. Please try again.",
        }));
      } finally {
        if (toastPayload) {
          window.setTimeout(() => {
            showToast(toastPayload.message, toastPayload.type);
          }, 0);
        }
        setProcessing((current) => ({ ...current, [field.key]: false }));
      }
    };

  const removeDocument = (field: DocumentUploadField<TValues>) => {
    if (disabled) {
      return;
    }

    const affectedFields = getAffectedFields(field);

    setFiles((prev) => {
      const next = { ...prev };
      affectedFields.forEach((affectedField) => {
        delete next[affectedField.key];
      });
      return next;
    });
    setPreviews((prev) => {
      const next = { ...prev };
      affectedFields.forEach((affectedField) => {
        const previewToClear = prev[affectedField.key];
        if (previewToClear) {
          URL.revokeObjectURL(previewToClear);
        }
        delete next[affectedField.key];
      });
      return next;
    });
    setRemovedInitialDocs((prev) => ({
      ...prev,
      ...affectedFields.reduce<Record<string, boolean>>((removed, affectedField) => {
        removed[String(affectedField.key)] = true;
        return removed;
      }, {}),
    }));

    const nextValues = updateCombinedGroupValues(field, { ...values, [field.key]: false }, false);
    const groupKey = field.combinedUploadGroup?.key;
    if (groupKey) {
      nextValues[groupKey] = false as TValues[typeof groupKey];
    }

    onChange(nextValues);
    onRemoveDocument?.(
      String(field.key),
      affectedFields.map((affectedField) => affectedField.key)
    );
  };

  const renderUploadCard = (
    field: DocumentUploadField<TValues>,
    options?: {
      title?: string;
      helper?: string;
      hideWrapper?: boolean;
      hidden?: boolean;
      showInlineError?: boolean;
    }
  ) => {
    const uploadedFile = files[field.key];
    const persistedDocument = removedInitialDocs[String(field.key)]
      ? null
      : initialDocuments?.[field.key];
    const isUploaded = values[field.key] === true;
    const isProcessing = processing[field.key] === true;
    const previewUrl = previews[field.key];
    const isPdf = uploadedFile?.type === "application/pdf";
    const persistedFileName = persistedDocument?.fileName || persistedDocument?.filename;
    const uploadedFileName = uploadedFile?.name || persistedFileName;
    const hasUploadedDocument = Boolean(uploadedFileName);
    const cardError = fieldErrors[field.key] || externalFieldErrors?.[field.key];
    const fileUrlToOpen = (persistedDocument as any)?.fileUrl || previewUrl;

    const card = (
      <div className={options?.hidden ? "hidden" : "space-y-2"}>
        <label
          className={`relative flex min-h-[132px] cursor-pointer flex-col items-center justify-center overflow-hidden rounded-xl border-2 border-dashed p-5 text-center transition hover:border-theme-primary hover:bg-theme-surface-soft ${
            isUploaded
              ? "border-theme-success bg-theme-success-soft"
              : "border-theme-border-strong bg-theme-surface"
          } ${cardError ? "border-theme-danger bg-theme-danger-soft" : ""} ${
            isProcessing ? "cursor-wait opacity-75" : ""
          } ${
            disabled
              ? "cursor-not-allowed opacity-70 hover:border-theme-border-strong hover:bg-theme-surface"
              : ""
          }`}
        >
          {isProcessing ? (
            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 bg-theme-surface/80">
              <Loader2 className="h-8 w-8 animate-spin text-theme-primary" />
              <p className="text-[10px] font-bold uppercase tracking-wider text-theme-primary">
                Analyzing...
              </p>
            </div>
          ) : null}

          {previewUrl && !isPdf ? (
            <>
              <div
                className="absolute inset-0 z-0"
                style={{
                  backgroundImage: `url(${previewUrl})`,
                  backgroundPosition: "center",
                  backgroundSize: "cover",
                }}
              />
              <div className="absolute inset-0 z-0 bg-theme-text/40" />
            </>
          ) : null}

          <div className="relative z-10 flex w-full flex-col items-center">
            {hasUploadedDocument ? (
              <CheckCircle2
                className={`mb-2 h-10 w-10 ${
                  previewUrl ? "text-theme-text-inverse" : "text-theme-primary"
                }`}
              />
            ) : (
              <span
                className={`mb-2 text-3xl ${
                  previewUrl ? "text-theme-text-inverse" : "text-theme-primary"
                }`}
              >
                {field.icon || <FileText className="h-9 w-9" />}
              </span>
            )}

            <p
              className={`text-[12px] font-semibold ${
                previewUrl ? "text-theme-text-inverse" : "text-theme-text-muted"
              }`}
            >
              {options?.title || field.title}
            </p>

            <div className="mt-2 w-full px-2">
              {!hasUploadedDocument ? (
                <p className="text-center text-xs text-theme-text-soft">
                  {options?.helper || field.helper || "Click to upload (PDF / JPG / PNG)"}
                </p>
              ) : (
                <div className="flex flex-col items-center">
                  <p
                    className={`mb-3 max-w-full wrap-break-word text-center text-xs font-medium leading-5 ${
                      previewUrl ? "text-theme-text-inverse" : "text-theme-primary"
                    }`}
                  >
                    {uploadedFileName}
                  </p>

                  {showPreviewButton || showDeleteButton ? (
                    <div className="flex gap-3">
                      {showPreviewButton ? (
                        <button
                          type="button"
                          disabled={disabled}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();

                            if (disabled) {
                              return;
                            }

                            onPreviewClick?.({
                              fieldKey: field.key,
                              url: fileUrlToOpen,
                              fileName: uploadedFileName,
                              field,
                              persistedDocument,
                            });

                            if (!onPreviewClick && fileUrlToOpen) {
                              window.open(fileUrlToOpen, "_blank");
                            }
                          }}
                          className="flex h-8 w-8 items-center justify-center rounded-full bg-white/80 shadow text-theme-primary transition-all hover:scale-105 hover:bg-white"
                          title="Preview Document"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      ) : null}

                      {showDeleteButton ? (
                        <button
                          type="button"
                          disabled={disabled}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();

                            if (disabled) {
                              return;
                            }

                            removeDocument(field);
                          }}
                          className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100 shadow text-red-600 transition-all hover:scale-105 hover:bg-red-200"
                          title="Remove Document"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>

          <input
            type="file"
            className="hidden"
            accept={accept}
            onChange={handleFileChange(field)}
            disabled={isProcessing || disabled}
          />
        </label>
        {options?.hideWrapper && options?.showInlineError !== false && cardError ? (
          <p className="text-xs text-theme-danger-bright">{cardError}</p>
        ) : null}
      </div>
    );

    if (options?.hideWrapper) {
      return card;
    }

    return (
      <WrapperInput label={field.label || ""} error={cardError} required={field.required}>
        {card}
      </WrapperInput>
    );
  };

  const renderedGroupIds = new Set<string>();

  const grid = (
    <div className="grid gap-4 md:grid-cols-2">
      {fields.map((field) => {
        const groupConfig = field.combinedUploadGroup;

        if (!groupConfig) {
          return <div key={String(field.key)}>{renderUploadCard(field)}</div>;
        }

        if (renderedGroupIds.has(groupConfig.id)) {
          return null;
        }

        renderedGroupIds.add(groupConfig.id);
        const groupFields = groupFieldsById[groupConfig.id] || [];
        const primaryField =
          groupFields.find((groupField) => groupField.combinedUploadGroup?.isPrimary) ||
          groupFields[0];
        const isCombined =
          combinedUploadGroups[groupConfig.id] === true ||
          Boolean(groupConfig.key && values[groupConfig.key] === true);
        const groupError =
          groupFields
            .map(
              (groupField) => fieldErrors[groupField.key] || externalFieldErrors?.[groupField.key]
            )
            .find(Boolean) ||
          (groupConfig.key ? externalFieldErrors?.[groupConfig.key] : undefined);

        return (
          <div key={groupConfig.id} className=" md:col-span-2">
            <WrapperInput error={groupError}>
              <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                  <div className="text-[12px] font-semibold text-theme-text">
                    {groupConfig.label}
                    {groupFields.some((groupField) => groupField.required) && (
                      <span className="text-red-500"> *</span>
                    )}
                  </div>
                  <Checkbox
                    color="blue"
                    checked={isCombined}
                    disabled={disabled}
                    label={groupConfig.checkboxLabel || "Both sides are in one file"}
                    labelClassName="text-sm font-medium text-theme-text"
                    wrapperClassName="shrink-0"
                    onChange={(event) => {
                      if (disabled) {
                        return;
                      }

                      const checked = event.target.checked;
                      setTouchedCombinedGroups((current) => ({
                        ...current,
                        [groupConfig.id]: true,
                      }));
                      setCombinedUploadGroups((current) => ({
                        ...current,
                        [groupConfig.id]: checked,
                      }));

                      if (!primaryField) {
                        return;
                      }

                      const nextValues = { ...values };
                      if (groupConfig.key) {
                        nextValues[groupConfig.key] = checked as TValues[typeof groupConfig.key];
                      }
                      groupFields.forEach((groupField) => {
                        const fieldHasOwnFile =
                          Boolean(files[groupField.key]) ||
                          Boolean(
                            !removedInitialDocs[String(groupField.key)] &&
                            initialDocuments?.[groupField.key]
                          );
                        const nextValue = checked
                          ? values[primaryField.key] === true
                          : fieldHasOwnFile;

                        nextValues[groupField.key] = nextValue as TValues[typeof groupField.key];
                      });
                      onChange(nextValues);
                    }}
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  {groupFields.map((groupField) => {
                    const fieldConfig = groupField.combinedUploadGroup;
                    const isHidden = isCombined && groupField.key !== primaryField?.key;
                    const isPrimary = groupField.key === primaryField?.key;

                    return (
                      <div key={String(groupField.key)}>
                        {renderUploadCard(groupField, {
                          hideWrapper: true,
                          showInlineError: false,
                          hidden: isHidden,
                          title:
                            isCombined && isPrimary
                              ? fieldConfig?.combinedTitle || groupField.title
                              : groupField.title,
                          helper:
                            isCombined && isPrimary
                              ? fieldConfig?.combinedHelper || groupField.helper
                              : groupField.helper,
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            </WrapperInput>
          </div>
        );
      })}
    </div>
  );

  if (!label && !description && !error) {
    return grid;
  }

  return (
    <WrapperInput label={label || ""} description={description} error={error}>
      {grid}
    </WrapperInput>
  );
}
